import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Conversation } from "@/types/conversation/conversation";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

interface ConversationNextStepsCardProps {
  conversation: Conversation;
}

interface NextStep {
  id: string;
  genieIcon: GenieIconName;
  title: string;
  body: string;
  cta: string;
  href: string;
}

/**
 * Contextual "what to do next" prompts driven by the state of this call:
 * unanalyzed, short, missing lead data, low sentiment, etc. Falls back to
 * a growth nudge ("get the embed") when the call is healthy.
 *
 * Standalone card extracted from ConversationDetailRail so it can live
 * alone in the new right column without dragging Manage/This-Week chrome
 * along with it. HG brand chrome per guidelines/design.md §6.
 */
export function ConversationNextStepsCard({ conversation }: ConversationNextStepsCardProps) {
  const agentId = conversation.agent_id;
  const details = conversation.details;
  const duration = details?.duration ?? 0;
  const leadInfo = details?.lead_info;
  const leadInfoEmpty = !leadInfo || (typeof leadInfo === "object" && Object.keys(leadInfo).length === 0);
  const isAnalyzed = conversation.isSynced;
  const goalsEmpty = !details?.goals || details.goals.length === 0;
  const sentimentScore = details?.analysis_result?.sentiment_analysis?.overall_sentiment_score;
  const lowSentiment = typeof sentimentScore === "number" && sentimentScore < 0.4;

  const steps: NextStep[] = useMemo(() => {
    const out: NextStep[] = [];

    if (!isAnalyzed) {
      out.push({
        id: "analyze",
        genieIcon: "eye",
        title: "Run the analyser",
        body: "Extract topics, lead info, and sentiment from this call.",
        cta: "Analyse",
        href: "#analyze",
      });
    }

    if (duration > 0 && duration < 60) {
      out.push({
        id: "short-call",
        genieIcon: "microphone",
        title: "Short call",
        body: "Under a minute. Review the welcome message. Callers may be dropping early.",
        cta: "Edit welcome",
        href: `/genie-hub/my-genies/${agentId}?section=section-welcome`,
      });
    }

    if (leadInfoEmpty && duration > 30) {
      out.push({
        id: "no-lead",
        genieIcon: "database",
        title: "No lead info captured",
        body: "Add required fields to Insights so future calls capture name, email, intent.",
        cta: "Edit insights",
        href: `/genie-hub/my-genies/${agentId}?section=section-insights`,
      });
    }

    if (goalsEmpty) {
      out.push({
        id: "no-goals",
        genieIcon: "target",
        title: "No insights tracked",
        body: "Define what success looks like for this genie. Insights progress shows up on every call.",
        cta: "Set up insights",
        href: `/genie-hub/my-genies/${agentId}?section=section-insights`,
      });
    }

    if (lowSentiment) {
      out.push({
        id: "low-sentiment",
        genieIcon: "trending-up",
        title: "Sentiment ran low",
        body: "Caller wasn't satisfied. Check the prompt and tone for this genie.",
        cta: "Tune the prompt",
        href: `/genie-hub/my-genies/${agentId}?section=section-prompts`,
      });
    }

    if (out.length === 0) {
      out.push({
        id: "embed",
        genieIcon: "share",
        title: "Get more calls like this",
        body: "Promote this genie. Embed it on your site or share the link with customers.",
        cta: "Get the embed",
        href: `/genie-hub/my-genies/${agentId}?section=section-distribution`,
      });
    }

    return out.slice(0, 3);
  }, [agentId, isAnalyzed, duration, leadInfoEmpty, goalsEmpty, lowSentiment]);

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 py-4 border-b border-[#4E9CFF]/10"
          style={{
            background: "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <GenieIcon
              name="lightbulb"
              size={24}
              color="#4E9CFF"
              secondaryColor="#FF4DB8"
              gradientEnabled
              label="Next steps"
            />
            <h3 className="text-base font-bold text-gray-900">Next steps</h3>
          </div>
        </div>

        <div className="px-5 py-5 space-y-3">
          {steps.map((step) => (
            <Link
              key={step.id}
              to={step.href}
              className="block p-[1.5px] rounded-xl bg-gradient-to-r from-[#4E9CFF]/30 to-[#FF4DB8]/30 hover:from-[#4E9CFF] hover:to-[#FF4DB8] transition-all"
            >
              <div className="rounded-[10px] bg-white p-3.5">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <GenieIcon
                      name={step.genieIcon}
                      size={22}
                      color="#4E9CFF"
                      secondaryColor="#FF4DB8"
                      gradientEnabled
                      label={step.title}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-900">{step.title}</div>
                    <div className="text-xs text-gray-600 mt-1 leading-relaxed">{step.body}</div>
                    <div className="inline-flex items-center gap-1 mt-2.5 text-xs">
                      <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent font-semibold">
                        {step.cta}
                      </span>
                      <ArrowRight className="w-3 h-3 text-[#FF4DB8]" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
